// 手机输入和消息工具分行呈现，保留清晰触控面积与可滚动详情；桌面布局及业务操作沿用原实现。
import React, { useEffect, useRef, useState } from "react";
import { Mic, Loader2 } from "lucide-react";
import { uploadChunked } from "../../lib/upload";

const MIMES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus", "audio/ogg"];
const extOf = (t) => ((t || "").includes("mp4") ? "m4a" : (t || "").includes("ogg") ? "ogg" : "webm");
export const voiceSupported = () => typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== "undefined";

/* 按住说话（鼠标 / 触屏统一 Pointer 事件）→ 松开发送语音消息：录音 → 分片上传对象存储 → onSend({type: voice, attachments, voice.duration})；
   手指 / 鼠标移出按钮再松开 = 取消；Esc 取消；60s 自动发送。Safari 走 audio/mp4。 */
export const VoiceRecorder = ({ api, zh, disabled, onSend, onError, className }) => {
  const [state, setState] = useState("idle"); // idle | rec | up
  const [sec, setSec] = useState(0);
  const [outside, setOutside] = useState(false);
  const mr = useRef(null);
  const chunks = useRef([]);
  const timer = useRef(null);
  const startAt = useRef(0);
  const cancelled = useRef(false);
  const btn = useRef(null);
  const T = (z, e) => (zh ? z : e);

  const stop = () => { clearInterval(timer.current); const m = mr.current; if (m && m.state !== "inactive") { try { m.stop(); } catch { /* */ } } };
  useEffect(() => { const k = (e) => { if (e.key === "Escape" && mr.current && mr.current.state !== "inactive") { cancelled.current = true; stop(); } }; window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, []);
  useEffect(() => () => { clearInterval(timer.current); try { mr.current?.stream?.getTracks().forEach((t) => t.stop()); } catch { /* */ } }, []);

  const start = async (e) => {
    if (state !== "idle" || disabled) return;
    e.preventDefault();
    if (!voiceSupported()) { onError?.(T("当前浏览器不支持录音（请用 Chrome / Safari 最新版）", "Recording not supported in this browser")); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = MIMES.find((t) => { try { return window.MediaRecorder.isTypeSupported(t); } catch { return false; } }) || "";
      const m = new window.MediaRecorder(stream, type ? { mimeType: type } : undefined);
      chunks.current = []; cancelled.current = false;
      m.ondataavailable = (ev) => { if (ev.data?.size) chunks.current.push(ev.data); };
      m.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const dur = (Date.now() - startAt.current) / 1000;
        const mime = m.mimeType || type || "audio/webm";
        const blob = new Blob(chunks.current, { type: mime });
        setOutside(false);
        if (cancelled.current) { setState("idle"); onError?.(T("已取消录音", "Recording cancelled"), "info"); return; }
        if (blob.size < 800 || dur < 0.7) { setState("idle"); onError?.(T("录音太短，请按住说话", "Too short — hold to talk")); return; }
        setState("up");
        try {
          const ext = extOf(mime);
          const file = new File([blob], `voice_${Date.now()}.${ext}`, { type: mime.split(";")[0] });
          const r = await uploadChunked(api, file, { type: file.type });
          await onSend({ type: "voice", body: "", attachments: [{ fileId: r.fileId, filename: r.filename || file.name, contentType: r.contentType || file.type, kind: "audio" }], voice: { duration: Math.round(dur * 10) / 10 } });
        } catch (err) { onError?.(err?.response?.data?.detail || T("语音发送失败，请重试", "Failed to send voice message")); }
        setState("idle");
      };
      m.start(250);
      mr.current = m; startAt.current = Date.now(); setState("rec"); setSec(0);
      try { btn.current?.setPointerCapture(e.pointerId); } catch { /* */ }
      timer.current = setInterval(() => setSec((s) => { if (s + 1 >= 60) stop(); return s + 1; }), 1000);
    } catch { onError?.(T("无法访问麦克风，请在浏览器授权后重试", "Mic access denied")); }
  };
  const move = (e) => {
    if (state !== "rec" || !btn.current) return;
    const r = btn.current.getBoundingClientRect();
    const out = e.clientX < r.left - 24 || e.clientX > r.right + 24 || e.clientY < r.top - 40 || e.clientY > r.bottom + 24;
    setOutside(out);
  };
  const release = () => { if (state !== "rec") return; if (outside) cancelled.current = true; stop(); };

  return (
    <span className="relative inline-flex">
      {state === "rec" && (
        <span data-testid="chat-voice-rec-hint" data-cancel={outside ? 1 : 0} className={`absolute bottom-full mb-2 right-0 whitespace-nowrap rounded-xl px-3 py-1.5 text-[11px] font-semibold shadow-lg ${outside ? "bg-rose-600 text-white" : "bg-slate-900 text-white"}`}>
          {outside ? T("松开取消", "Release to cancel") : T(`录音中 ${sec}s · 松开发送 · 移开取消`, `Recording ${sec}s · release to send · move away to cancel`)}
        </span>
      )}
      <button ref={btn} type="button" data-testid="chat-voice-btn" data-state={state} disabled={disabled || state === "up"} aria-label={T("按住说话", "Hold to talk")}
        onPointerDown={start} onPointerMove={move} onPointerUp={release} onPointerCancel={release} onContextMenu={(e) => e.preventDefault()}
        title={T("按住说话 · 松开发送语音消息（自动转文字）", "Hold to talk · release to send")}
        className={className || `select-none touch-none h-11 min-w-11 sm:h-8 sm:min-w-8 px-1 rounded-xl inline-flex items-center justify-center gap-0.5 text-slate-500 hover:bg-white hover:text-[#4F6BFF] disabled:opacity-50 ${state === "rec" ? (outside ? "!bg-rose-600 !text-white" : "!bg-red-500 !text-white animate-pulse") : ""}`}>
        {state === "up" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
        {state === "rec" && <span className="text-[10px] tabular-nums">{sec}s</span>}
      </button>
    </span>
  );
};
